/* Earth's Way — Global JS
   Lightweight, no framework. ES modules-ready. */

(() => {
  'use strict';

  // ── Cart drawer ─────────────────────────────────────────────
  const CartDrawer = {
    drawer: null,
    backdrop: null,
    refreshToken: 0,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-backdrop]');
      if (!this.drawer) return;

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        const closer = e.target.closest('[data-cart-close]');
        if (opener) { e.preventDefault(); this.open(); }
        if (closer) { e.preventDefault(); this.close(); }
        if (e.target === this.backdrop) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.hasAttribute('open')) this.close();
      });

      document.addEventListener('cart:updated', () => this.refresh());
    },

    open() {
      if (!this.drawer) return;
      this.drawer.setAttribute('open', '');
      this.backdrop.setAttribute('data-open', '');
      document.body.style.overflow = 'hidden';
      this.refresh();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('open');
      this.backdrop.removeAttribute('data-open');
      document.body.style.overflow = '';
    },

    // Re-render the drawer contents from the server. `cart-drawer` must stay a
    // section: the Section Rendering API cannot render snippets.
    async refresh() {
      const token = ++this.refreshToken;
      try {
        const res = await fetch('/?section_id=cart-drawer');
        if (!res.ok) throw new Error(`Cart drawer section ${res.status}`);
        const text = await res.text();
        // A slower earlier refresh must not overwrite a newer one's result.
        if (token !== this.refreshToken) return;

        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-cart-body]');
        const target = this.drawer.querySelector('[data-cart-body]');
        if (fresh && target) target.innerHTML = fresh.innerHTML;

        const freshFooter = doc.querySelector('[data-cart-footer]');
        const footer = this.drawer.querySelector('[data-cart-footer]');
        if (freshFooter && footer) footer.innerHTML = freshFooter.innerHTML;
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    }
  };

  // The count lives in the header, not the drawer — keep it updating on pages
  // where no drawer is rendered.
  const renderCartCount = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });
  };

  // ── Cart API helpers ────────────────────────────────────────
  const Cart = {
    async add(items) {
      const formData = { items: Array.isArray(items) ? items : [items] };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const data = await res.json();
      await this.fetchAndDispatch();
      return data;
    },
    async change(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Cart change failed');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async fetchAndDispatch() {
      const res = await fetch('/cart.js');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    }
  };

  window.EarthsWay = window.EarthsWay || {};
  window.EarthsWay.Cart = Cart;
  window.EarthsWay.CartDrawer = CartDrawer;

  // ── Reveal animations ──────────────────────────────────────
  const observeReveals = () => {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -80px 0px' });
    document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
  };

  // ── Leaf burst micro-interaction (add to cart) ─────────────
  const burstLeaves = (origin) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = origin.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    for (let i = 0; i < 8; i++) {
      const leaf = document.createElement('span');
      leaf.className = 'ew-leaf-particle';
      leaf.textContent = '🌿';
      const angle = (Math.PI * 2 * i) / 8 + Math.random() * 0.5;
      const dist = 60 + Math.random() * 70;
      leaf.style.left = cx + 'px';
      leaf.style.top = cy + 'px';
      leaf.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
      leaf.style.setProperty('--dy', (Math.sin(angle) * dist - 40) + 'px');
      leaf.style.setProperty('--rot', (Math.random() * 360 - 180) + 'deg');
      document.body.appendChild(leaf);
      leaf.addEventListener('animationend', () => leaf.remove());
    }
  };

  // ── Add-to-cart forms ──────────────────────────────────────
  const handleAddForms = () => {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-add-to-cart-form]');
      if (!form) return;
      e.preventDefault();

      const button = form.querySelector('[type="submit"]');
      const originalText = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.loadingText || 'Bezig…';
      }

      try {
        const formData = new FormData(form);
        const items = {
          id: formData.get('id'),
          quantity: formData.get('quantity') || 1
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) items.selling_plan = sellingPlan;

        await Cart.add(items);
        if (button) {
          burstLeaves(button);
          button.textContent = button.dataset.successText || 'Toegevoegd ✓';
        }
        CartDrawer.open();
      } catch (err) {
        console.error(err);
        if (button) button.textContent = 'Probeer opnieuw';
      } finally {
        if (button) {
          button.disabled = false;
          setTimeout(() => { button.textContent = originalText; }, 1200);
        }
      }
    });
  };

  // ── Quantity inputs ────────────────────────────────────────
  const handleQty = () => {
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-qty-minus]');
      const plus = e.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;
      const wrap = (minus || plus).closest('.qty');
      const input = wrap?.querySelector('input');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      let v = parseInt(input.value || '1', 10);
      v = minus ? Math.max(min, v - 1) : Math.min(max, v + 1);
      input.value = v;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  // ── Cart line items: quantity change + remove ──────────────
  const handleCartLines = () => {
    let qtyTimer = null;

    // Quantity changed (typed or via +/− buttons) → sync the line.
    // Debounced so rapid +/− clicks coalesce into one request. A quantity
    // of 0 removes the line from the cart.
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      const line = parseInt(input.dataset.line, 10);
      let qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(() => {
        Cart.change(line, qty).catch((err) => console.error('Cart change failed', err));
      }, 280);
    });

    // Remove (trash) button → delete the line.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-remove]');
      if (!btn) return;
      e.preventDefault();
      const line = parseInt(btn.dataset.line, 10);
      Cart.change(line, 0).catch((err) => console.error('Cart remove failed', err));
    });
  };

  // ── Init ───────────────────────────────────────────────────
  const init = () => {
    document.addEventListener('cart:updated', (e) => renderCartCount(e.detail));
    CartDrawer.init();
    observeReveals();
    handleAddForms();
    handleQty();
    handleCartLines();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   INTERACTIVE: animated counters
   ============================================================ */
(function() {
  if (!('IntersectionObserver' in window)) return;
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      if (el.dataset.counted === 'true') return;
      el.dataset.counted = 'true';
      const target = parseFloat(el.dataset.counter);
      const duration = parseInt(el.dataset.duration || 1600, 10);
      const decimals = parseInt(el.dataset.decimals || 0, 10);
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      const startTime = performance.now();
      function step(now) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = target * eased;
        el.textContent = prefix + (decimals ? value.toFixed(decimals) : Math.floor(value).toLocaleString('nl-NL')) + suffix;
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = prefix + (decimals ? target.toFixed(decimals) : target.toLocaleString('nl-NL')) + suffix;
      }
      requestAnimationFrame(step);
    });
  }, { threshold: 0.4 });
  counters.forEach((c) => obs.observe(c));
})();

/* ============================================================
   INTERACTIVE: 3D card tilt (subtle)
   ============================================================ */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window) return;
  document.querySelectorAll('[data-tilt]').forEach((card) => {
    let raf;
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        card.style.transform = `perspective(900px) rotateY(${x * 3}deg) rotateX(${-y * 3}deg) translateZ(0)`;
      });
    });
    card.addEventListener('mouseleave', () => {
      cancelAnimationFrame(raf);
      card.style.transform = '';
    });
  });
})();

/* ============================================================
   INTERACTIVE: magnetic buttons
   ============================================================ */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window) return;
  document.querySelectorAll('[data-magnetic]').forEach((btn) => {
    let raf;
    const strength = parseFloat(btn.dataset.magnetic) || 0.3;
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
    });
    btn.addEventListener('mouseleave', () => {
      cancelAnimationFrame(raf);
      btn.style.transform = '';
    });
  });
})();

/* ============================================================
   INTERACTIVE: sticky add-to-cart on PDP
   ============================================================ */
(function() {
  const buyForm = document.querySelector('[data-add-to-cart-form]');
  const stickyBar = document.querySelector('[data-sticky-atc]');
  if (!buyForm || !stickyBar) return;

  // Show the bar once the main add-to-cart button has scrolled above the
  // viewport, hide it again when it scrolls back into view. A direct scroll
  // check is deterministic and avoids IntersectionObserver edge cases.
  let ticking = false;
  function update() {
    const rect = buyForm.getBoundingClientRect();
    const scrolledPast = rect.bottom < 0;
    stickyBar.classList.toggle('is-visible', scrolledPast);
    ticking = false;
  }
  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();

  // Sticky button proxies a click to the main form's submit button.
  const stickyAdd = stickyBar.querySelector('[data-sticky-add]');
  if (stickyAdd) {
    stickyAdd.addEventListener('click', (e) => {
      e.preventDefault();
      const submit = buyForm.querySelector('button[type="submit"]');
      if (submit) submit.click();
    });
  }
})();

/* ============================================================
   INTERACTIVE: parallax on hero media (subtle)
   ============================================================ */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const targets = document.querySelectorAll('[data-parallax]');
  if (!targets.length) return;
  let scrollY = window.scrollY;
  let ticking = false;
  function update() {
    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const speed = parseFloat(el.dataset.parallax) || 0.15;
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed;
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    });
    ticking = false;
  }
  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
  update();
})();

/* ============================================================
   INTERACTIVE: split-text reveal (character-by-character)
   ============================================================ */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;
  document.querySelectorAll('[data-split-text]').forEach((el) => {
    const text = el.textContent;
    const words = text.split(/\s+/);
    el.innerHTML = words.map((w, i) =>
      `<span class="split-word" style="--i:${i}"><span class="split-inner">${w}</span></span>`
    ).join(' ');
  });
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target);
      }
    });
  }, { threshold: 0.3 });
  document.querySelectorAll('[data-split-text]').forEach((el) => obs.observe(el));
})();

/* ============================================================
   INTERACTIVE: marquee pause on hover
   ============================================================ */
(function() {
  document.querySelectorAll('[data-marquee]').forEach((m) => {
    m.addEventListener('mouseenter', () => m.classList.add('is-paused'));
    m.addEventListener('mouseleave', () => m.classList.remove('is-paused'));
  });
})();

/* ============================================================
   INTERACTIVE: scroll progress indicator at top of page
   ============================================================ */
(function() {
  const bar = document.createElement('div');
  bar.className = 'ew-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  let ticking = false;
  function update() {
    const h = document.documentElement;
    const scrolled = h.scrollTop;
    const max = h.scrollHeight - h.clientHeight;
    const pct = max > 0 ? (scrolled / max) * 100 : 0;
    bar.style.width = pct + '%';
    ticking = false;
  }
  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
  update();
})();
